import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Modal, Button } from "react-bootstrap";
import Moment from "moment";
import { useLocation, useNavigate } from "react-router-dom";
import { apiHandler } from "../../../api/apihandler";
import {
  DAILY_REPORTS_LIST,
  NOTIFICATION_LIST,
  TEACHERS_GET_ASSIGNED_GAMES_API,
  STUDENT_GAME_WEEKLY_REPORT,
  ACCIDENT_REPORTS_LIST,
  ILLNESS_REPORTS_LIST,
} from "../../../api/constants";
import Announcement from "../../../components/announcement";
import LearningGameCard from "../../../components/common/LearningGameCard";
import { getEffectiveStudentId, isParentLoginProfile, getSelectedChildProfile, getParentChildren, getStoredParentStudentId, setStoredParentStudentId } from "../../../utilities/teacherModulePermissions";
import { setUserData } from "../../../store/setting/actions";
import "../../../assets/css/student-dashboard.css";

function stripHtml(value) {
  if (!value) return "";
  return String(value).replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
}

// report_date comes back from the API as a full ISO instant (e.g.
// "2026-09-18T18:30:00.000+05:30") carrying the API server's own timezone
// offset, even though it's meant to represent a plain calendar date - which
// daycare day this report is for, not a moment in time. Moment(reportDate)
// re-interprets that instant into the VIEWER'S browser-local timezone before
// any day-level comparison happens, which silently shifts the date backward
// a day for anyone far enough behind the server's offset (US/Canada viewing
// an India-hosted server, for example) - "Today" renders as "Yesterday" for
// them while it's correct for a viewer whose offset happens to be close to
// the server's. Reading only the leading YYYY-MM-DD digits and parsing that
// as a plain local date sidesteps the conversion entirely, so every viewer
// sees the same calendar date the server actually meant, regardless of their
// own timezone.
function parseReportDate(reportDate) {
  const dateOnly = String(reportDate || "").slice(0, 10);
  return Moment(dateOnly, "YYYY-MM-DD", true);
}

// Same YYYY-MM-DD-prefix comparison parseReportDate uses, but as a plain
// string check with no Moment strict-parse in the way - a strict parse
// silently returns an invalid Moment (matching nothing) the moment the
// source string's format varies even slightly, which is a worse failure mode
// than a plain string compare for something as simple as "same calendar day".
function isSameReportDate(firstDate, secondDate) {
  const first = String(firstDate || "").slice(0, 10);
  const second = String(secondDate || "").slice(0, 10);
  return Boolean(first) && Boolean(second) && first === second;
}

function parseMinutes(value) {
  if (!value) return 0;
  const parsed = Moment(value, ["HH:mm:ss", "HH:mm"], true);
  if (!parsed.isValid()) return 0;
  return parsed.hours() * 60 + parsed.minutes();
}

function formatTime(value) {
  if (!value) return "--";
  const parsed = Moment(value, ["HH:mm:ss", "HH:mm", Moment.ISO_8601], true);
  if (!parsed.isValid()) return "--";
  return parsed.format("h:mm A");
}

function sleepHours(report) {
  const sleeps = Array.isArray(report?.daily_report_sleeps) ? report.daily_report_sleeps : [];
  const total = sleeps.reduce((sum, item) => {
    const start = parseMinutes(item?.start_time);
    const end = parseMinutes(item?.end_time);
    if (!start || !end || end <= start) return sum;
    return sum + (end - start);
  }, 0);
  return `${(total / 60).toFixed(1)}h`;
}

function getApiList(payload) {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  return [];
}

function getMealAmountLabel(value) {
  if (typeof value === "string" && value.trim()) return value.trim();
  const map = {
    0: "None",
    1: "Some",
    2: "Half",
    3: "Most",
    4: "All",
    5: "All+",
  };
  return map[Number(value)] || "Some";
}

function getMealAmountPercent(value) {
  if (value === null || value === undefined || value === "") return 0;

  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    const labelMap = {
      none: 0,
      some: 25,
      half: 50,
      most: 75,
      all: 90,
      "all+": 100,
    };

    if (Object.prototype.hasOwnProperty.call(labelMap, normalized)) {
      return labelMap[normalized];
    }
  }

  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return 0;
  if (numeric >= 0 && numeric <= 5) return Math.min(100, numeric * 20);
  return Math.max(0, Math.min(100, numeric));
}

function getMealAmountStep(value) {
  if (value === null || value === undefined || value === "") return 0;

  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    const labelMap = {
      none: 0,
      some: 1,
      half: 2,
      most: 3,
      all: 4,
      "all+": 5,
    };

    if (Object.prototype.hasOwnProperty.call(labelMap, normalized)) {
      return labelMap[normalized];
    }
  }

  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return 0;
  if (numeric >= 0 && numeric <= 5) return numeric;
  return Math.max(0, Math.min(5, Math.round(numeric / 20)));
}

const MEAL_PROGRESS_LABELS = ["None", "Some", "Half", "Most", "All", "All+"];

function flattenDailyReportMeals(groups = []) {
  if (!Array.isArray(groups)) return [];

  return groups.flatMap((group) => {
    if (Array.isArray(group?.items)) {
      const mealTypeName = group?.menu_type || group?.meal_type || group?.meal_type_name || "Meal";
      return group.items.map((item) => ({
        ...item,
        meal_type_name: item?.meal_type_name || mealTypeName,
      }));
    }

    return [group];
  });
}

function getProfileMetaLine(profile) {
  const parts = [];
  const level =
    profile?.class_student?.class?.class_name ||
    profile?.class_student?.class?.name ||
    profile?.user_setting?.education_level;
  const room = profile?.user_teachers_and_room?.room?.room_name || profile?.class_student?.room?.room_name;
  const school = profile?.organization?.organization_name || profile?.organization_name || profile?.school_name;
  if (level) parts.push(level);
  if (room) parts.push(room);
  if (school) parts.push(school);
  return parts.length ? parts.join(" · ") : "Student care log";
}

function moodColor(text) {
  const value = String(text || "").toLowerCase();
  if (value.includes("happy") || value.includes("calm") || value.includes("settled")) return "#1D9E75";
  if (value.includes("quiet") || value.includes("sad") || value.includes("upset")) return "#378ADD";
  return "#888";
}

function diaperTag(check) {
  if (Number(check) === 1) return { label: "D/T", className: "dcl-tag-dt" };
  if (Number(check) === 3) return { label: "Wet", className: "dcl-tag-wet" };
  if (Number(check) === 4) return { label: "BM", className: "dcl-tag-bm" };
  return { label: "Dry", className: "dcl-tag-dry" };
}

const sectionList = ["mood", "sleep", "eating", "diaper", "incident", "medication", "note", "games", "completedGames", "weeklyReport", "overview", "bringMore", "learned", "observation", "comments", "illness"];
function getBringMoreLabel(thingId) {
  const labels = { 1: "Diapers", 2: "Wipes", 3: "Ointment", 4: "Clothes", 5: "Blanket", 6: "Other" };
  return labels[Number(thingId)] || "Other";
}

function SectionEmpty({ icon, text }) {
  return (
    <div className="dcl-section-empty">
      <div className="dcl-section-empty-icon">{icon}</div>
      <div className="dcl-section-empty-text">{text}</div>
    </div>
  );
}



function StudentDashboard() {
  const rawProfile = useSelector((state) => state.setting.setting.user_data);
  const profileData = useMemo(() => {
    if (!rawProfile) return {};
    if (typeof rawProfile === "object") return rawProfile;
    try {
      return JSON.parse(rawProfile);
    } catch (error) {
      return {};
    }
  }, [rawProfile]);

  const studentId = useMemo(() => getEffectiveStudentId(profileData), [profileData]);
  const dispatch = useDispatch();
  // A parent with more than one linked child lands here without a selection
  // made yet (sign-in.js/login-with-otp.js only auto-pick when there's exactly
  // one child) — block the dashboard behind a "select a child" prompt until
  // they pick one. Changing child later happens from the profile screen.
  const parentChildren = getParentChildren(profileData);
  const needsChildSelection = isParentLoginProfile(profileData) && parentChildren.length > 1 && !getStoredParentStudentId();

  function handleSelectChild(childId) {
    setStoredParentStudentId(childId);
    dispatch(setUserData(JSON.stringify({ ...profileData, selected_student_id: childId })));
  }

  const navigate = useNavigate();
  const location = useLocation();

  // report_date (preferred, e.g. from the reports list) seeds which day this
  // opens on; reportId (older deep links, e.g. a notification) has no date
  // to seed with under the per-day model below, so it falls back to today -
  // this screen no longer preloads a batch of reports to look an id up in.
  const targetReportDate = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("report_date") || null;
  }, [location.search]);

  const todayDateOnly = useMemo(() => Moment().format("YYYY-MM-DD"), []);

  const [notifications, setNotifications] = useState([]);
  const [assignedGames, setAssignedGames] = useState([]);
  const [completedGames, setCompletedGames] = useState([]);
  const [weeklyReport, setWeeklyReport] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAcknowledging, setIsAcknowledging] = useState(false);
  const [selectedReportIndex, setSelectedReportIndex] = useState(0);
  const [showHistoricalReport, setShowHistoricalReport] = useState(false);
  const [accidentReports, setAccidentReports] = useState([]);
  const [illnessReports, setIllnessReports] = useState([]);
  const [selectedDateOnly, setSelectedDateOnly] = useState(() => targetReportDate || todayDateOnly);
  const [currentReport, setCurrentReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(true);
  const [dayCompletedGames, setDayCompletedGames] = useState([]);
  // Bumped by the "Refresh report" button to force a re-fetch of the same
  // date (selectedDateOnly alone wouldn't change, so wouldn't retrigger the
  // effect below on its own).
  const [refreshTick, setRefreshTick] = useState(0);
  const [openSections] = useState(() => {
    const state = {};
    sectionList.forEach((key) => {
      state[key] = true;
    });
    return state;
  });

  function openGame(game) {
    if (!game) {
      navigate("/dashboard/app/game/game-category");
      return;
    }

    const gameNameId = game?.game_name_id || game?.game_name?.id;
    const categoryId = game?.game_name?.category?.id ?? game?.game_name?.category_id;

    if (!gameNameId || !categoryId) {
      navigate("/dashboard/app/game/game-category");
      return;
    }

    // AI-adaptive games live on the Adaptive Games tab - open it there so
    // the game's category is shown and auto-play finds it.
    const mode = Number(game?.game_name?.is_ai_generated) === 1 ? "adaptive" : "static";
    navigate(`/dashboard/app/game/game-category?gameNameId=${gameNameId}&categoryId=${categoryId}&autoPlay=1&mode=${mode}`);
  }

  async function loadNotifications() {
    try {
      const notificationPayload = { page: 1, limit: 30 };
      if (studentId) {
        notificationPayload.student_id = studentId;
      }

      const notificationRes = await apiHandler(NOTIFICATION_LIST, "POST", notificationPayload);

      if (notificationRes?.data?.response?.status === 200) {
        setNotifications(getApiList(notificationRes?.data?.data?.notifications));
      } else {
        setNotifications([]);
      }
    } catch (error) {
      console.error(error);
      setNotifications([]);
    }
  }

  // Loads exactly one day's report on demand (Previous/Next, or the initial
  // date) instead of preloading a batch of reports and paging an in-memory
  // index - dayCompletedGames rides along from the same request, since it
  // comes bundled with whichever report_date range was asked for.
  async function loadReportForDate(dateOnly) {
    setReportLoading(true);
    try {
      const payload = { page: 1, limit: 1, start_date: dateOnly, end_date: dateOnly };
      if (studentId) {
        payload.student_id = studentId;
      }

      const response = await apiHandler(DAILY_REPORTS_LIST, "POST", payload);
      if (response?.data?.response?.status === 200) {
        const reports = getApiList(response?.data?.data?.data);
        const completedGamesList = Array.isArray(response?.data?.data?.completed_games)
          ? response.data.data.completed_games
          : [];
        setCurrentReport(reports[0] || null);
        setDayCompletedGames(completedGamesList);
      } else {
        setCurrentReport(null);
        setDayCompletedGames([]);
      }
    } catch (error) {
      console.error(error);
      setCurrentReport(null);
      setDayCompletedGames([]);
    } finally {
      setReportLoading(false);
    }
  }

  async function loadAssignedGames(dateOnly) {
    if (!studentId) return;
    try {
      // The assigned-games endpoint takes attendance_date, not a
      // start_date/end_date range like the accident/illness reports APIs.
      const response = await apiHandler(TEACHERS_GET_ASSIGNED_GAMES_API, "POST", {
        student_id: studentId,
        user_id: studentId,
        ...(dateOnly ? { attendance_date: dateOnly } : {}),
      });
      if (response?.data?.response?.status === 200) {
        setAssignedGames(response?.data?.data?.assigned_games || []);
      } else {
        setAssignedGames([]);
      }
    } catch (error) {
      console.error(error);
      setAssignedGames([]);
    }
  }

  async function loadWeeklyReport() {
    try {
      const payload = {};
      if (studentId) {
        payload.student_id = studentId;
      }
      const response = await apiHandler(STUDENT_GAME_WEEKLY_REPORT, "POST", payload);
      if (response?.data?.response?.status === 200) {
        setWeeklyReport(response?.data?.data?.report || null);
      } else {
        setWeeklyReport(null);
      }
    } catch (error) {
      console.error(error);
      setWeeklyReport(null);
    }
  }

  // form_accident_reports has no created_at column, and this endpoint's
  // start_date/end_date filter runs against created_at - passing them 500s
  // the request ("Unknown column 'created_at'"), so this fetches a batch
  // unfiltered and gets scoped to the viewed date client-side (via
  // incident_date, the column that actually exists) instead.
  async function loadAccidentReports() {
    if (!studentId) return;
    try {
      const response = await apiHandler(ACCIDENT_REPORTS_LIST, "POST", {
        type: 1,
        page: 1,
        limit: 100,
        student_id: studentId,
      });
      if (response?.data?.response?.status === 200) {
        setAccidentReports(getApiList(response?.data?.data?.data));
      } else {
        setAccidentReports([]);
      }
    } catch (error) {
      console.error(error);
      setAccidentReports([]);
    }
  }

  // Same reasoning as loadAccidentReports above - avoid start_date/end_date
  // here too in case illness reports hits the same missing-column error on
  // its own table.
  async function loadIllnessReports() {
    if (!studentId) return;
    try {
      const response = await apiHandler(ILLNESS_REPORTS_LIST, "POST", {
        page: 1,
        limit: 100,
        student_id: studentId,
      });
      if (response?.data?.response?.status === 200) {
        setIllnessReports(getApiList(response?.data?.data?.data));
      } else {
        setIllnessReports([]);
      }
    } catch (error) {
      console.error(error);
      setIllnessReports([]);
    }
  }

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
    if (localStorage.getItem("uzoneAuthToken") && !needsChildSelection) {
      loadAssignedGames();
      loadWeeklyReport();
      loadNotifications();
    }
  }, [studentId, needsChildSelection]);

  useEffect(() => {
    setSelectedDateOnly(targetReportDate || todayDateOnly);
  }, [targetReportDate, todayDateOnly]);

  const latestReport = currentReport;

  // Scoped to the viewed date - refetches whenever Previous/Next changes
  // which date is on screen, loading just that one day's data on demand
  // instead of a preloaded batch.
  useEffect(() => {
    if (!localStorage.getItem("uzoneAuthToken") || needsChildSelection) return;
    loadReportForDate(selectedDateOnly);
    loadAssignedGames(selectedDateOnly);
  }, [studentId, needsChildSelection, selectedDateOnly, refreshTick]);

  // Unscoped (see the missing-column note on the functions themselves) - a
  // fresh student only needs this once, not on every date change.
  useEffect(() => {
    if (!localStorage.getItem("uzoneAuthToken") || needsChildSelection) return;
    loadAccidentReports();
    loadIllnessReports();
  }, [studentId, needsChildSelection]);
  const hasDailyReport = Boolean(latestReport);
  const shouldShowMissingTodayState = !reportLoading && !hasDailyReport && selectedDateOnly === todayDateOnly;
  const shouldShowMissingOtherDateState = !reportLoading && !hasDailyReport && selectedDateOnly !== todayDateOnly;
  // A parent viewing this dashboard should see the selected child's name/room,
  // not their own — the parent's own record has no class/room fields at all.
  const displayProfile = isParentLoginProfile(profileData) ? (getSelectedChildProfile(profileData) || {}) : profileData;
  const studentName = `${displayProfile?.first_name || "Student"} ${displayProfile?.last_name || ""}`.trim();
  const metaLine = getProfileMetaLine(displayProfile);

  const reportDate = parseReportDate(selectedDateOnly).isValid()
    ? parseReportDate(selectedDateOnly)
    : Moment();

  const reportDayDiff = Moment().startOf("day").diff(reportDate.clone().startOf("day"), "days");
  const dateLabel = reportDayDiff === 0
    ? `Today, ${reportDate.format("MMM D")}`
    : reportDayDiff === 1
      ? `Yesterday, ${reportDate.format("MMM D")}`
      : reportDate.format("ddd, MMM D");

  // Previous always steps back a day; Next steps forward but can't go past
  // today - there's nothing to show beyond it.
  const canGoPrevious = true;
  const canGoNext = selectedDateOnly < todayDateOnly;

  const moods = (latestReport?.daily_report_fealings || [])
    .map((item) => item?.fealing?.name)
    .filter(Boolean)
    .slice(0, 5);

  const mealGroups = Array.isArray(latestReport?.daily_report_meals) ? latestReport.daily_report_meals : [];
  const meals = flattenDailyReportMeals(mealGroups);
  const sleeps = Array.isArray(latestReport?.daily_report_sleeps) ? latestReport.daily_report_sleeps : [];
  const diapers = Array.isArray(latestReport?.daily_report_diapers) ? latestReport.daily_report_diapers : [];
  const bringMores = Array.isArray(latestReport?.daily_report_bring_mores) ? latestReport.daily_report_bring_mores : [];

  const unreadNotifications = notifications.filter((item) => Number(item?.is_read) !== 1);
  // Notification objects carry their text in `description` (see notification.js,
  // the notification bell page, which reads elm?.description) - this used to
  // filter/read `.message`, a field these objects don't have.
  const medicationAlerts = unreadNotifications.filter((item) => /medication|medicine|dose/i.test(String(item?.description || "")));

  // Incidents shows real accident report records, not a notification
  // heuristic - a submitted report has no guarantee of an unread
  // notification still sitting around matching a keyword regex. Scoped to
  // the viewed report's date, same as Observation.
  const currentDateAccidentReports = accidentReports.filter((report) =>
    isSameReportDate(report?.incident_date, selectedDateOnly),
  );

  // Illness reports have no dedicated incident-date field - `created_at` is
  // what the dedicated Illness Reports page itself displays as the report's
  // date, so that's what this is scoped against.
  const currentDateIllnessReports = illnessReports.filter((report) =>
    isSameReportDate(report?.created_at, selectedDateOnly),
  );

  // The backend doesn't scope this field by start_date/end_date - it's an
  // all-time completed-games list bundled with the daily report response,
  // not a per-day one. Show it as returned by the API, same as assigned games.
  const currentDateCompletedGames = dayCompletedGames;

  const mealCards = mealGroups.map((group, index) => {
    const groupLabel = group?.menu_type || group?.meal_type || group?.meal_type_name || `Meal ${index + 1}`;
    const items = Array.isArray(group?.items) ? group.items : [];
    const normalizedItems = items.map((item, itemIndex) => {
      const mealName = stripHtml(item?.menu_name || item?.meal_name || item?.name || `Meal item ${itemIndex + 1}`) || `Meal item ${itemIndex + 1}`;
      return {
        id: item?.id || item?.menu_meal_id || `${groupLabel}-${itemIndex}`,
        name: mealName,
        time: formatTime(item?.meal_time),
        amountLabel: getMealAmountLabel(item?.meal_amount),
        amountStep: getMealAmountStep(item?.meal_amount),
        percent: getMealAmountPercent(item?.meal_amount),
      };
    });

    return {
      label: groupLabel,
      count: normalizedItems.length,
      eatenLabel: normalizedItems.length ? `${normalizedItems.length} item${normalizedItems.length !== 1 ? "s" : ""} logged` : "Not logged",
      items: normalizedItems,
    };
  });

  const summaryMood = moods[0] || "Calm";
  const recentCompletedGames = currentDateCompletedGames;

  function viewAccidentReports() {
    navigate("/dashboard/accident-reports");
  }

  function viewIllnessReports() {
    navigate("/dashboard/illness-reports");
  }

  return (
    <>
      <Modal show={needsChildSelection} backdrop="static" keyboard={false} centered>
        <Modal.Header>
          <Modal.Title style={{ fontSize: '18px' }}>Select a Child</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p style={{ color: '#6b7280', fontSize: '14px' }}>
            You have more than one child linked to your account. Select which child's dashboard you'd like to view — you can change this any time from your profile.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {parentChildren.map((child) => (
              <Button
                key={child?.id}
                variant="outline-primary"
                className="d-flex align-items-center"
                style={{ gap: '10px', padding: '10px 14px', textAlign: 'left' }}
                onClick={() => handleSelectChild(child?.id)}
              >
                <img
                  src={child?.user_setting?.photo || 'https://uzone360live.s3.amazonaws.com/noimage.png'}
                  alt=""
                  style={{ width: '32px', height: '32px', borderRadius: '50%', objectFit: 'cover' }}
                />
                <span>{child?.first_name} {child?.last_name}</span>
              </Button>
            ))}
          </div>
        </Modal.Body>
      </Modal>
      <div id="content-page" className="content-page dcl-page">
      <Announcement maxWidth="740px" outerPadding="12px 0 6px" showAuthorMeta={false} showCloseButton={true} />
      <div className="dcl-wrap">
        <div className="dcl-top-bar">
          <div className="dcl-child-avi">
            {displayProfile?.user_setting?.photo ? (
              <img
                src={displayProfile.user_setting.photo}
                alt=""
                style={{ width: "100%", height: "100%", borderRadius: "50%", objectFit: "cover" }}
              />
            ) : (
              studentName.split(" ").map((part) => part?.[0] || "").join("").slice(0, 2).toUpperCase()
            )}
          </div>
          <div>
            <div className="dcl-child-name">{studentName}</div>
            <div className="dcl-child-meta">{hasDailyReport ? metaLine : `${metaLine} · Daily report pending`}</div>
          </div>
          <div className="dcl-date-nav">
            <button
              className="dcl-date-btn"
              onClick={() => setSelectedDateOnly((prev) => Moment(prev, "YYYY-MM-DD").subtract(1, "day").format("YYYY-MM-DD"))}
              disabled={reportLoading}
            >‹</button>
            <div className="dcl-date-lbl">{dateLabel}</div>
            <button
              className="dcl-date-btn"
              onClick={() => canGoNext && setSelectedDateOnly((prev) => Moment(prev, "YYYY-MM-DD").add(1, "day").format("YYYY-MM-DD"))}
              disabled={!canGoNext || reportLoading}
            >›</button>
          </div>
        </div>

        {reportLoading && !hasDailyReport ? (
          <section className="dcl-empty-state">
            <div className="dcl-empty-text">Loading daily report…</div>
          </section>
        ) : hasDailyReport ? (
          <>
            <div className="dcl-alert-banner">
              <div className="dcl-alert-icon">Rx</div>
              <div>
                <div className="dcl-alert-title">Medication administered today</div>
                <div className="dcl-alert-body">
                  {stripHtml(medicationAlerts[0]?.description) || "No medication update was logged today."}
                </div>
              </div>
            </div>

            <div className="dcl-summary-grid">
              <div className="dcl-sum-card"><div className="dcl-sum-val">{sleepHours(latestReport)}</div><div className="dcl-sum-lbl">Total nap time</div></div>
              <div className="dcl-sum-card"><div className="dcl-sum-val">{meals.length}</div><div className="dcl-sum-lbl">Meals and snacks</div></div>
              <div className="dcl-sum-card"><div className="dcl-sum-val">{diapers.length}</div><div className="dcl-sum-lbl">Diaper checks</div></div>
              <div className="dcl-sum-card"><div className="dcl-sum-val dcl-mood-green">{summaryMood}</div><div className="dcl-sum-lbl">Overall mood</div></div>
            </div>

            <section className="dcl-section">
          <div className="dcl-section-header">
            <div className="dcl-section-icon dcl-bg-purple">🪪</div>
            <div className="dcl-section-title">Report overview</div>
          </div>
          {openSections.overview ? (
            <div className="dcl-section-body">
              <div className="dcl-diaper-row">
                <div className="dcl-diaper-type">Report ID</div>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#1a1a18" }}>#{latestReport?.id ?? "--"}</div>
              </div>
              <div className="dcl-diaper-row">
                <div className="dcl-diaper-type">Feelings</div>
                <span
                  className="dcl-diaper-tag"
                  style={{
                    background: `${moodColor(moods[0])}22`,
                    color: moodColor(moods[0]),
                  }}
                >
                  {moods.length ? moods.join(", ") : "Not recorded"}
                </span>
              </div>
            </div>
          ) : null}
        </section>

        <section className="dcl-section">
          <div className="dcl-section-header">
            <div className="dcl-section-icon dcl-bg-purple">🎒</div>
            <div className="dcl-section-title">Please bring more</div>
            <span className="dcl-section-badge dcl-bg-purple">{bringMores.length} items</span>
          </div>
          {openSections.bringMore ? (
            <div className="dcl-section-body">
              {bringMores.length ? (
                <div className="dcl-mood-row">
                  {bringMores.map((item, idx) => (
                    <div className="dcl-diaper-tag dcl-tag-amber" key={`bring-more-${item?.id || idx}`}>
                      {getBringMoreLabel(item?.things)}
                      {item?.other ? `: ${stripHtml(item.other)}` : ""}
                    </div>
                  ))}
                </div>
              ) : (
                <SectionEmpty icon="🎒" text="Nothing requested today" />
              )}
            </div>
          ) : null}
        </section>

        {stripHtml(latestReport?.learned) ? (
          <section className="dcl-section">
            <div className="dcl-section-header">
              <div className="dcl-section-icon dcl-bg-green">🎓</div>
              <div className="dcl-section-title">What they learned</div>
            </div>
            {openSections.learned ? (
              <div className="dcl-section-body">
                <div className="dcl-note-box">
                  <div className="dcl-note-text">{stripHtml(latestReport?.learned)}</div>
                </div>
              </div>
            ) : null}
          </section>
        ) : null}

        {stripHtml(latestReport?.observation) ? (
          <section className="dcl-section">
            <div className="dcl-section-header">
              <div className="dcl-section-icon dcl-bg-blue">👁</div>
              <div className="dcl-section-title">Observation</div>
            </div>
            {openSections.observation ? (
              <div className="dcl-section-body">
                <div className="dcl-note-box">
                  <div className="dcl-note-text">{stripHtml(latestReport?.observation)}</div>
                  <div className="dcl-note-sig">Educator note · {reportDate.format("h:mm A")}</div>
                </div>
              </div>
            ) : null}
          </section>
        ) : null}

        <section className="dcl-section">
          <div className="dcl-section-header">
            <div className="dcl-section-icon dcl-bg-green">🍽</div>
            <div className="dcl-section-title">Meals</div>
            <span className="dcl-section-badge dcl-bg-green">{meals.length ? `${meals.length} items logged` : "No entries"}</span>
          </div>
          {openSections.eating ? (
            <div className="dcl-section-body">
              {mealCards.length ? (
                <div className="dcl-meal-grid dcl-meal-grid-nutrition">
                  {mealCards.map((meal, idx) => (
                    <div className="dcl-meal-card dcl-meal-card-rich" key={`meal-${idx}`}>
                      <div className="dcl-meal-card-top">
                        <div className="dcl-meal-label">{meal.label}</div>
                        <div className="dcl-meal-count">{meal.count} item{meal.count !== 1 ? "s" : ""}</div>
                      </div>
                      <div className="dcl-eaten-lbl">{meal.eatenLabel}</div>
                      <div className="dcl-meal-item-list">
                        {meal.items.length ? meal.items.map((mealItem) => (
                          <div className="dcl-meal-item" key={mealItem.id}>
                            <div className="dcl-meal-item-copy">
                              <div className="dcl-meal-item-name">{mealItem.name}</div>
                              <div className="dcl-meal-item-meta">{mealItem.time}</div>
                              <div className="dcl-meal-progress-wrap">
                                <input
                                  type="range"
                                  min={0}
                                  max={5}
                                  step={1}
                                  value={mealItem.amountStep}
                                  readOnly
                                  className="dcl-meal-progress-input"
                                />
                                <div className="dcl-meal-progress-scale">
                                  {MEAL_PROGRESS_LABELS.map((label) => (
                                    <span key={`${mealItem.id}-${label}`}>{label}</span>
                                  ))}
                                </div>
                              </div>
                            </div>
                            <div className="dcl-meal-item-amount">{mealItem.amountLabel}</div>
                          </div>
                        )) : (
                          <SectionEmpty icon="🍽" text="No meal items recorded" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <SectionEmpty icon="🍽" text="No meals recorded" />
              )}
            </div>
          ) : null}
        </section>

        <section className="dcl-section">
          <div className="dcl-section-header">
            <div className="dcl-section-icon dcl-bg-purple">💬</div>
            <div className="dcl-section-title">Comments</div>
          </div>
          {openSections.comments ? (
            <div className="dcl-section-body">
              {stripHtml(latestReport?.comments) ? (
                <div className="dcl-note-box">
                  <div className="dcl-note-text">{stripHtml(latestReport?.comments)}</div>
                </div>
              ) : (
                <SectionEmpty icon="💬" text="No comments" />
              )}
            </div>
          ) : null}
        </section>

        <section className="dcl-section">
          <div className="dcl-section-header">
            <div className="dcl-section-icon dcl-bg-amber">📋</div>
            <div className="dcl-section-title">Notes</div>
          </div>
          {openSections.notes ? (
            <div className="dcl-section-body">
              {stripHtml(latestReport?.notes) ? (
                <div className="dcl-note-box">
                  <div className="dcl-note-text">{stripHtml(latestReport?.notes)}</div>
                </div>
              ) : (
                <SectionEmpty icon="📋" text="No notes" />
              )}
            </div>
          ) : null}
        </section>

        <section className="dcl-section">
          <div className="dcl-section-header">
            <div className="dcl-section-icon dcl-bg-blue">💤</div>
            <div className="dcl-section-title">Sleep</div>
            <span className="dcl-section-badge dcl-bg-blue">{sleeps.length} naps · {latestReport ? sleepHours(latestReport) : "0.0h"}</span>
          </div>
          {openSections.sleep ? (
            <div className="dcl-section-body">
              {sleeps.length ? (
                sleeps.map((entry, idx) => (
                  <div className="dcl-diaper-row" key={`sleep-${idx}`}>
                    <div className="dcl-diaper-type">Nap</div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "#1a1a18" }}>
                      {formatTime(entry?.start_time)} - {formatTime(entry?.end_time)}
                    </div>
                  </div>
                ))
              ) : (
                <SectionEmpty icon="💤" text="No naps recorded" />
              )}
            </div>
          ) : null}
        </section>

        <section className="dcl-section">
          <div className="dcl-section-header">
            <div className="dcl-section-icon dcl-bg-amber">🚼</div>
            <div className="dcl-section-title">Diapers / Toilet</div>
            <span className="dcl-section-badge dcl-bg-amber">{diapers.length} checks</span>
          </div>
          {openSections.diaper ? (
            <div className="dcl-section-body">
              {diapers.length ? (
                diapers.map((entry, idx) => {
                  const tag = diaperTag(entry?.diaper_check);
                  return (
                    <div className="dcl-diaper-row" key={`diaper-${idx}`}>
                      <div className="dcl-diaper-time">{formatTime(entry?.diaper_time)}</div>
                      <div className="dcl-diaper-type">Routine check</div>
                      <span className={`dcl-diaper-tag ${tag.className}`}>{tag.label}</span>
                    </div>
                  );
                })
              ) : (
                <SectionEmpty icon="🚼" text="No diaper checks recorded" />
              )}
            </div>
          ) : null}
        </section>

        <section className="dcl-section">
          <div className="dcl-section-header">
            <div className="dcl-section-icon dcl-bg-blue">💊</div>
            <div className="dcl-section-title">Medication</div>
            <span className="dcl-section-badge dcl-bg-green">{medicationAlerts.length} dose logged</span>
          </div>
          {openSections.medication ? (
            <div className="dcl-section-body">
              {medicationAlerts.length ? (
                <div className="dcl-med-box">
                  <div className="dcl-med-header">
                    <div className="dcl-med-name">Medication update</div>
                    <span className="dcl-med-status">Given</span>
                  </div>
                  <div className="dcl-med-details">
                    <div className="dcl-med-detail"><strong>Date:</strong> {reportDate.format("MMM D, YYYY")}</div>
                    <div className="dcl-med-detail"><strong>Time:</strong> {medicationAlerts[0]?.created_at ? Moment(medicationAlerts[0].created_at).format("h:mm A") : "--"}</div>
                    <div className="dcl-med-detail"><strong>Source:</strong> Notification</div>
                    <div className="dcl-med-detail"><strong>Status:</strong> Recorded</div>
                  </div>
                  <div className="dcl-note-box">
                    <div className="dcl-note-text">{stripHtml(medicationAlerts[0]?.description)}</div>
                    <div className="dcl-note-sig">Recorded by educator</div>
                  </div>
                </div>
              ) : (
                <SectionEmpty icon="💊" text="No medication logged today" />
              )}
            </div>
          ) : null}
        </section>
          </>
        ) : shouldShowMissingTodayState ? (
          <section className="dcl-empty-state">
            <div className="dcl-empty-icon">📅</div>
            <div className="dcl-empty-title">Today&apos;s daily report is not available</div>
            <div className="dcl-empty-text">
              No daily report has been submitted for today yet. You can view a report from another day,
              or refresh this page later once today&apos;s report is published.
            </div>
            <div className="dcl-empty-actions">
              <button
                className="dcl-empty-btn dcl-empty-btn-primary"
                onClick={() => setRefreshTick((tick) => tick + 1)}
                disabled={reportLoading}
              >
                {reportLoading ? "Refreshing..." : "Refresh report"}
              </button>
            </div>
          </section>
        ) : shouldShowMissingOtherDateState ? (
          <section className="dcl-empty-state">
            <div className="dcl-empty-icon">🗒</div>
            <div className="dcl-empty-title">Daily report not available yet</div>
            <div className="dcl-empty-text">
              No daily care log has been submitted for {studentName} on {reportDate.format("MMM D, YYYY")}.
            </div>
            <div className="dcl-empty-actions">
              <button className="dcl-empty-btn dcl-empty-btn-primary" onClick={() => setRefreshTick((tick) => tick + 1)} disabled={reportLoading}>
                {reportLoading ? "Refreshing..." : "Refresh report"}
              </button>
              <button className="dcl-empty-btn" onClick={() => navigate("/dashboard/app/game/game-category")}>
                Browse games
              </button>
            </div>
          </section>
        ) : null}

        {currentDateAccidentReports.length > 0 ? (
          <section className="dcl-section">
            <div className="dcl-section-header">
              <div className="dcl-section-icon dcl-bg-amber">⚠</div>
              <div className="dcl-section-title">Incidents and accidents</div>
              <span className="dcl-section-badge dcl-bg-amber">{currentDateAccidentReports.length} incident</span>
            </div>
            {openSections.incident ? (
              <>
                <div className="dcl-section-body">
                  <div className="dcl-incident-box">
                    <div className="dcl-inc-header">
                      <div className="dcl-inc-dot"></div>
                      <div className="dcl-inc-title">
                        Incident on {parseReportDate(currentDateAccidentReports[0]?.incident_date).format("MMM D, YYYY")}
                      </div>
                      <div className="dcl-inc-time">{formatTime(currentDateAccidentReports[0]?.incident_time)}</div>
                    </div>
                    <div className="dcl-med-detail"><strong>Location:</strong> {stripHtml(currentDateAccidentReports[0]?.location) || "N/A"}</div>
                    <div className="dcl-inc-body">
                      {stripHtml(currentDateAccidentReports[0]?.incident_description)}
                    </div>
                    <div className="dcl-inc-sig">
                      {currentDateAccidentReports[0]?.parent_signature ? "Signed" : "Pending Signature"}
                    </div>
                  </div>
                </div>
                <div className="dcl-sign-row">
                  <div className="dcl-sign-lbl">
                    View the full accident report to review or sign it
                  </div>
                  <button className="dcl-sign-btn" onClick={viewAccidentReports}>
                    View accident reports
                  </button>
                </div>
              </>
            ) : null}
          </section>
        ) : null}

        {currentDateIllnessReports.length > 0 ? (
          <section className="dcl-section">
            <div className="dcl-section-header">
              <div className="dcl-section-icon dcl-bg-amber">🤒</div>
              <div className="dcl-section-title">Illness reports</div>
              <span className="dcl-section-badge dcl-bg-amber">{currentDateIllnessReports.length} report</span>
            </div>
            {openSections.illness ? (
              <>
                <div className="dcl-section-body">
                  <div className="dcl-incident-box">
                    <div className="dcl-inc-header">
                      <div className="dcl-inc-dot"></div>
                      <div className="dcl-inc-title">
                        Illness on {parseReportDate(currentDateIllnessReports[0]?.created_at).format("MMM D, YYYY")}
                      </div>
                    </div>
                    <div className="dcl-med-detail"><strong>Symptoms:</strong> {stripHtml(currentDateIllnessReports[0]?.symptoms) || "N/A"}</div>
                    <div className="dcl-med-detail"><strong>Expected return:</strong> {currentDateIllnessReports[0]?.return_date ? parseReportDate(currentDateIllnessReports[0].return_date).format("MMM D, YYYY") : "N/A"}</div>
                    <div className="dcl-inc-body">
                      {stripHtml(currentDateIllnessReports[0]?.requested_action_parent) || "No requested action recorded."}
                    </div>
                    <div className="dcl-inc-sig">
                      {currentDateIllnessReports[0]?.parent_signature ? "Signed" : "Pending Signature"}
                    </div>
                  </div>
                </div>
                <div className="dcl-sign-row">
                  <div className="dcl-sign-lbl">
                    View the full illness report to review or sign it
                  </div>
                  <button className="dcl-sign-btn" onClick={viewIllnessReports}>
                    View illness reports
                  </button>
                </div>
              </>
            ) : null}
          </section>
        ) : null}

        {assignedGames.length > 0 ? (
          <section className="dcl-section">
            <div className="dcl-section-header">
              <div className="dcl-section-icon dcl-bg-green">🎮</div>
              <div className="dcl-section-title">Assigned Learning Games</div>
              <span className="dcl-section-badge dcl-bg-green">{assignedGames.length} assigned</span>
            </div>
            {openSections.games ? (
              <div className="dcl-section-body">
                <div className="dcl-games-grid">
                  {assignedGames.map((game, idx) => (
                    <LearningGameCard key={`game-${game?.id || idx}`} game={game} onPlay={openGame} />
                  ))}
                </div>
              </div>
            ) : null}
          </section>
        ) : null}

        <section className="dcl-section">
          <div className="dcl-section-header">
            <div className="dcl-section-icon dcl-bg-blue">🏆</div>
            <div className="dcl-section-title">Completed Learning Games</div>
            <span className="dcl-section-badge dcl-bg-blue">{currentDateCompletedGames.length} completed</span>
          </div>
          {openSections.completedGames ? (
            <div className="dcl-section-body">
              {recentCompletedGames.length ? (
                <div className="dcl-games-grid">
                  {recentCompletedGames.map((game, idx) => (
                    <LearningGameCard
                      key={`completed-${game?.id || idx}`}
                      game={game}
                      completed
                      level={game?.level || game?.game_name?.level || 1}
                      score={game?.score ?? "--"}
                      onPlay={openGame}
                    />
                  ))}
                </div>
              ) : (
                <SectionEmpty icon="🏆" text="No completed games yet" />
              )}
            </div>
          ) : null}
        </section>

        <section className="dcl-section">
          <div className="dcl-section-header">
            <div className="dcl-section-icon dcl-bg-blue">📈</div>
            <div className="dcl-section-title">Weekly progress report</div>
            {weeklyReport?.week_start_date ? (
              <span className="dcl-section-badge dcl-bg-blue">
                Week of {Moment(weeklyReport.week_start_date).format("MMM D")}
              </span>
            ) : null}
            <span className={`dcl-chevron ${openSections.weeklyReport ? "" : "closed"}`}>⌄</span>
          </div>
          {openSections.weeklyReport ? (
            <div className="dcl-section-body">
              {weeklyReport ? (
                <>
                  {Array.isArray(weeklyReport.weeklySummary) && weeklyReport.weeklySummary.length > 0 ? (
                    <div className="mb-4">
                      <h6 className="text-primary">
                        <i className="ri-gamepad-line me-2"></i>
                        This week's play
                      </h6>
                      <div className="ms-4">
                        {weeklyReport.weeklySummary.map((row, idx) => (
                          <div key={idx} className="mb-1">
                            {row.category}: {row.sessionsPlayed} session{row.sessionsPlayed === 1 ? "" : "s"},{" "}
                            {row.accuracyPercent}% accuracy, difficulty {row.difficultyReached} of 5
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : null}

                  {Array.isArray(weeklyReport.strengths) && weeklyReport.strengths.length > 0 ? (
                    <div className="mb-4">
                      <h6 className="text-success">
                        <i className="ri-star-line me-2"></i>
                        Strengths
                      </h6>
                      <ul className="ms-4">
                        {weeklyReport.strengths.map((item, idx) => (
                          <li key={idx}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  ) : null}

                  {Array.isArray(weeklyReport.priorities) && weeklyReport.priorities.length > 0 ? (
                    <div className="mb-4">
                      <h6 style={{ color: "#6f42c1" }}>
                        <i className="ri-focus-3-line me-2"></i>
                        Focus areas
                      </h6>
                      <ul className="ms-4">
                        {weeklyReport.priorities.map((item, idx) => (
                          <li key={idx}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  ) : null}

                  {Array.isArray(weeklyReport.activities) && weeklyReport.activities.length > 0 ? (
                    <div className="mb-4">
                      <h6 className="text-info">
                        <i className="ri-hand-heart-line me-2"></i>
                        Try at home
                      </h6>
                      <ul className="ms-4">
                        {weeklyReport.activities.map((item, idx) => (
                          <li key={idx}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  ) : null}

                  {weeklyReport.parent_note ? (
                    <div className="dcl-note-box">
                      <div className="dcl-note-text">{weeklyReport.parent_note}</div>
                    </div>
                  ) : null}
                </>
              ) : (
                <p className="text-muted mb-0">
                  No weekly report yet - keep playing the practice games and one will show up here.
                </p>
              )}
            </div>
          ) : null}
        </section>
      </div>
      </div>
    </>
  );
}

export default StudentDashboard;
